import { UserNotification, UserNotificationState } from "@api/core.models";
import type { ActivityViewerDialogData } from "./feed/public/activity/activity-viewer.component";
import { MatDialog } from "@angular/material/dialog";
import { format } from "date-fns";
import { NOTIFICATION_TIME_FORMAT, PT_BR_DATE_LOCALE } from "@ui/date-formats";
import type { FormViewerDialogData } from "./feed/public/form/smart-form-viewer.component";

export class Notification {
    id: string;
    icon: string;
    title: string;
    subtitle: string;
    userNotification: UserNotification;

    constructor(id, icon, title, subtitle, userNotification) {
        this.id = id;
        this.icon = icon;
        this.title = title;
        this.subtitle = subtitle;
        this.userNotification = userNotification;
    }

    get isRead() {
        return this.userNotification.state === UserNotificationState._1;
    }
}

export type ActivityNotificationDataType = "Valor.Health.Engage.Application.Notification.Data.ActivityNotificationData";

export type FormEntryNotificationDataType =
    "Valor.Health.Engage.Application.Notification.Data.FormEntryNotificationData";

export class ActivityNotificationParser {
    static Type: ActivityNotificationDataType =
        "Valor.Health.Engage.Application.Notification.Data.ActivityNotificationData";

    constructor(private _matDialog: MatDialog) {}

    parse(userNotification): Notification {
        const subtitle = format(new Date(userNotification.notification.creationTime), NOTIFICATION_TIME_FORMAT, {
            locale: PT_BR_DATE_LOCALE,
        });

        return new Notification(
            userNotification.id,
            "assignment_turned_in",
            "Resposta de atividade",
            subtitle,
            userNotification
        );
    }

    async open(notification: Notification) {
        let data: ActivityViewerDialogData = {
            itemId: notification.userNotification.notification.data.properties.itemId,
            timestamp: notification.userNotification.notification.data.properties.timestamp,
            patientName: notification.userNotification.notification.data.properties.patientInfo.name,
            patientId: notification.userNotification.notification.data.properties.patientInfo.id,
        };
        // Loaded on demand: NotificationService is in the initial bundle and the viewers pull in vforms and
        // Material tables. The declaring module comes too because the viewers are not standalone. (VS-1961)
        const [{ ActivityViewerComponent }] = await Promise.all([
            import("./feed/public/activity/activity-viewer.component"),
            import("./feed/public/public-shared.module"),
        ]);
        this._matDialog.open(ActivityViewerComponent, {
            panelClass: "activity-viewer-dialog",
            data,
        });
    }
}

export class FormEntryNotificationParser {
    static Type: FormEntryNotificationDataType =
        "Valor.Health.Engage.Application.Notification.Data.FormEntryNotificationData";

    constructor(private _matDialog: MatDialog) {}

    parse(userNotification): Notification {
        const subtitle = format(new Date(userNotification.notification.creationTime), NOTIFICATION_TIME_FORMAT, {
            locale: PT_BR_DATE_LOCALE,
        });

        return new Notification(
            userNotification.id,
            "assignment_turned_in",
            "Resposta de formulário - Health Engage",
            subtitle,
            userNotification
        );
    }

    async open(notification: Notification) {
        let data: FormViewerDialogData = {
            itemId: notification.userNotification.notification.data.properties.itemId,
            formId: notification.userNotification.notification.data.properties.formId,
            timestamp: notification.userNotification.notification.data.properties.timestamp,
            patientName: notification.userNotification.notification.data.properties.patientInfo.name,
            patientId: notification.userNotification.notification.data.properties.patientInfo.id,
        };

        // Loaded on demand for the same reason as the activity viewer above. (VS-1961)
        const [{ SmartFormViewerComponent }] = await Promise.all([
            import("./feed/public/form/smart-form-viewer.component"),
            import("./feed/public/public-shared.module"),
        ]);
        this._matDialog.open(SmartFormViewerComponent, {
            height: "96vh",
            panelClass: "form-preview-dialog",
            data,
        });
    }
}
